import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { i18n } from '@/plugins/i18n';
import { LanguageEnum } from '@/enums/appEnum';
import { storageUtils } from '@/utils';
import { useWindowSize } from '@vueuse/core';
import { isSystemDarkMode, watchSystemTheme } from '@/utils/theme.utils';
import avatarLight from '@/assets/nav/avatar/default-avatar-light.png';
import avatarDark from '@/assets/nav/avatar/default-avatar.png';

const useAppStore = defineStore(
  'app',
  () => {
    // 记住密码
    const rememberPasswordItem = storageUtils.getItem('rememberPassword');
    const rememberPassword = ref(rememberPasswordItem ? JSON.parse(rememberPasswordItem) : false);

    const setRememberPassword = (value: boolean) => {
      rememberPassword.value = value;
      storageUtils.setItem('rememberPassword', value.toString());
    };

    const getRememberPassword = () => {
      return rememberPassword.value;
    };
    // 侧边栏是否折叠
    const sidebarCollapse = ref(false);

    // 当前语言
    const language = ref<string>(storageUtils.getItem('locale') || LanguageEnum.EN);

    // 主题色
    const themeColor = ref('#409EFF');

    // 是否跟随系统主题
    const followSystemTheme = ref(true);

    // 是否暗色模式（默认根据系统主题初始化）
    const isDark = ref(followSystemTheme.value ? isSystemDarkMode() : false);

    // 是否固定头部
    const fixedHeader = ref(true);

    // 是否显示侧边栏Logo
    const sidebarLogo = ref(true);

    // 是否显示标签视图
    const tagsView = ref(true);

    // 是否刷新页面
    const isPageRefresh = ref(true);

    //是否显示进度条
    const showNprogress = ref(true);

    // 切换进度条显示
    const toggleShowNprogress = () => {
      showNprogress.value = !showNprogress.value;
    };

    // 是否是移动端
    const { width } = useWindowSize();

    const isMobile = computed(() => {
      const ua = navigator.userAgent.toLowerCase();

      const isMobileUA = /mobile|android|iphone|ipad|ipod/.test(ua);

      return width.value <= 500 || isMobileUA;
    });

    // 应用暗色模式
    const applyDarkMode = (dark: boolean) => {
      if (dark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    // 初始化时应用暗色模式
    applyDarkMode(isDark.value);

    const defaultAvatarImage = computed(() => {
      return isDark.value ? avatarDark : avatarLight;
    });

    // 检测系统主题变化（当启用跟随系统时）
    let unwatchSystemTheme: (() => void) | null = null;

    const setupSystemThemeWatch = () => {
      if (followSystemTheme.value) {
        unwatchSystemTheme = watchSystemTheme((systemIsDark) => {
          isDark.value = systemIsDark;
        });
      } else {
        // 取消监听
        if (unwatchSystemTheme) {
          unwatchSystemTheme();
          unwatchSystemTheme = null;
        }
      }
    };

    // 初始化系统主题监听
    setupSystemThemeWatch();

    // 监听暗色模式变化
    watch(isDark, (newVal) => {
      applyDarkMode(newVal);
    });

    // 监听跟随系统主题设置的变化
    watch(followSystemTheme, (newVal) => {
      if (newVal) {
        // 启用跟随系统，立即同步系统主题
        isDark.value = isSystemDarkMode();
      }

      setupSystemThemeWatch();
    });

    // 切换侧边栏
    const toggleSidebar = () => {
      sidebarCollapse.value = !sidebarCollapse.value;
    };

    // 设置侧边栏状态
    const setSidebarCollapse = (collapse: boolean) => {
      sidebarCollapse.value = collapse;
    };

    // 切换语言
    const setLanguage = (lang: string) => {
      language.value = lang;
      i18n.global.locale.value = lang as LanguageEnum;
      storageUtils.setItem('locale', lang);
    };

    // 设置主题色
    const setThemeColor = (color: string) => {
      themeColor.value = color;
    };

    // 切换暗色模式
    const toggleDark = () => {
      // 手动切换时，自动关闭跟随系统主题
      followSystemTheme.value = false;
      isDark.value = !isDark.value;
    };

    // 设置是否跟随系统主题
    const setFollowSystemTheme = (follow: boolean) => {
      followSystemTheme.value = follow;
    };

    // 手动设置暗色模式（会关闭跟随系统）
    const setDarkMode = (dark: boolean) => {
      followSystemTheme.value = false;
      isDark.value = dark;
    };

    // 设置固定头部
    const setFixedHeader = (fixed: boolean) => {
      fixedHeader.value = fixed;
    };

    // 设置侧边栏Logo
    const setSidebarLogo = (show: boolean) => {
      sidebarLogo.value = show;
    };

    // 设置标签视图
    const setTagsView = (show: boolean) => {
      tagsView.value = show;
    };

    // 刷新页面
    const reloadPage = () => {
      isPageRefresh.value = !isPageRefresh.value;
    };

    return {
      sidebarCollapse,
      language,
      themeColor,
      isDark,
      followSystemTheme,
      fixedHeader,
      sidebarLogo,
      tagsView,
      isPageRefresh,
      toggleSidebar,
      setSidebarCollapse,
      setLanguage,
      setThemeColor,
      toggleDark,
      setDarkMode,
      setFollowSystemTheme,
      setFixedHeader,
      setSidebarLogo,
      setTagsView,
      reloadPage,

      showNprogress,
      toggleShowNprogress,

      isMobile,

      defaultAvatarImage,

      rememberPassword,
      setRememberPassword,
      getRememberPassword
    };
  },
  {
    persist: {
      key: 'app-store',
      storage: sessionStorage,
      paths: ['language', 'themeColor', 'isDark', 'followSystemTheme', 'fixedHeader', 'sidebarLogo', 'tagsView']
    }
  }
);

export default useAppStore;
